import React, { useState } from 'react';
import './add-employee.css';
import { createEmployee } from '../../../services/admin/api';
import PopupModel from '../../../components/popup-model/PopupModel.jsx';

const initialState = {
  username: '',
  companyname: '',
  email: '',
  gender: '',
  dateOfBirth: '',
  contactNumber: '',
};

const initialErrors = {
  username: '',
  companyname: '',
  email: '',
  gender: '',
  dateOfBirth: '',
  contactNumber: '',
};

const formatDateToDDMMYYYY = (dateStr) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const AddEmployee = () => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);

  const [modalInfo, setModalInfo] = useState({
    show: false,
    title: '',
    message: '',
    isSuccess: true,
  });

  const showModal = (title, message, isSuccess) => {
    setModalInfo({ show: true, title, message, isSuccess });
  };

  const closeModal = () => {
    setModalInfo({ ...modalInfo, show: false });
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'username':
        if (!value.trim()) error = 'Username is mandatory.';
        else if (!/^[A-Za-z ]{1,40}$/.test(value)) error = 'Username must contain only alphabets and spaces, up to 40 characters.';
        break;
      case 'companyname':
        if (!value.trim()) error = 'Company name is mandatory.';
        else if (!/^[A-Za-z ]{1,40}$/.test(value)) error = 'Company name must contain only alphabets and spaces, up to 40 characters.';
        break;
      case 'email':
        if (!value.trim()) error = 'Email is mandatory.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Invalid email format.';
        break;
      case 'contactNumber':
        if (!value.trim()) error = 'Phone number is mandatory.';
        else if (!/^[0-9]{10}$/.test(value)) error = 'Phone number must be exactly 10 digits.';
        break;
      case 'gender':
        if (!value) error = 'Gender is required.';
        break;
      case 'dateOfBirth':
        if (!value) error = 'Date of birth is required.';
        else {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          selectedDate.setHours(0, 0, 0, 0);
          if (selectedDate >= today) error = 'Date of birth must be in the past.';
        }
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...initialErrors };

    for (const key in form) {
      const error = validateField(key, form[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showModal('Validation Error', 'Please correct the errors before submitting.', false);
      return;
    }

    try {
      const payload = {
        ...form,
        dateOfBirth: formatDateToDDMMYYYY(form.dateOfBirth),
      };

      const result = await createEmployee(payload);
      showModal('Success', result.message || 'Employee created successfully!', true);
      setForm(initialState);
      setErrors(initialErrors);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        err?.error ||
        'Something went wrong while adding employee.';
      showModal('Error', errorMessage, false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-xl rounded-xl mt-8 font-sans">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-gray-700 font-medium mb-1">Username</label>
            <input
              id="username"
              name="username"
              value={form.username}
              onChange={handleChange}
              className={`mt-1 w-full border ${errors.username ? 'border-red-500' : 'border-gray-300'} rounded-md px-4 py-2`}
              placeholder="Enter username (up to 40 chars)"
              maxLength="40"
            />
            {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username}</p>}
          </div>

          <div>
            <label htmlFor="companyname" className="block text-gray-700 font-medium mb-1">Company Name</label>
            <input
              id="companyname"
              name="companyname"
              value={form.companyname}
              onChange={handleChange}
              className={`mt-1 w-full border ${errors.companyname ? 'border-red-500' : 'border-gray-300'} rounded-md px-4 py-2`}
              placeholder="Enter company name (up to 40 chars)"
              maxLength="40"
            />
            {errors.companyname && <p className="text-red-500 text-sm mt-1">{errors.companyname}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-gray-700 font-medium mb-1">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className={`mt-1 w-full border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md px-4 py-2`}
              placeholder="example@mail.com"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="contactNumber" className="block text-gray-700 font-medium mb-1">Contact Number</label>
            <input
              id="contactNumber"
              name="contactNumber"
              type="tel"
              value={form.contactNumber}
              onChange={handleChange}
              className={`mt-1 w-full border ${errors.contactNumber ? 'border-red-500' : 'border-gray-300'} rounded-md px-4 py-2`}
              placeholder="Enter 10-digit contact number"
              maxLength="10"
              pattern="[0-9]{10}"
              title="Phone number must be exactly 10 digits"
            />
            {errors.contactNumber && <p className="text-red-500 text-sm mt-1">{errors.contactNumber}</p>}
          </div>

          <div>
            <label htmlFor="gender" className="block text-gray-700 font-medium mb-1">Gender</label>
            <select
              id="gender"
              name="gender"
              value={form.gender}
              onChange={handleChange}
              className={`mt-1 w-full border ${errors.gender ? 'border-red-500' : 'border-gray-300'} rounded-md px-4 py-2`}
            >
              <option value="">Select gender</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
              <option value="OTHERS">Others</option>
            </select>
            {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
          </div>

          <div>
            <label htmlFor="dateOfBirth" className="block text-gray-700 font-medium mb-1">Date of Birth</label>
            <input
              id="dateOfBirth"
              name="dateOfBirth"
              type="date"
              value={form.dateOfBirth}
              onChange={handleChange}
              className={`mt-1 w-full border ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'} rounded-md px-4 py-2`}
              max={new Date().toISOString().split('T')[0]}
            />
            {errors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>}
          </div>
        </div>

        <br />

        <div className="text-right">
          <button
            type="submit"
            className="popup-close-btn success"
          >
            Add Agent
          </button>
        </div>
      </form>

      <PopupModel
        show={modalInfo.show}
        onClose={closeModal}
        title={modalInfo.title}
        message={modalInfo.message}
        isSuccess={modalInfo.isSuccess}
      />
    </div>
  );
};

export default AddEmployee;
